import styled from "@emotion/styled";
import { Volume2, VolumeX } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { FOOTBALL_RESET_EVENT } from "./footballShot";

const sounds = {
  menu: { file: "/audio/menuSwish.mp3", gain: 0.23 },
  kick: { file: "/audio/ballKick.mp3", gain: 0.3 },
  goal: { file: "/audio/goalCheer.mp3", gain: 0.6 },
} as const;
type Cue = keyof typeof sounds;
type SoundState = {
  enabled: boolean;
  ready: boolean;
  toggle: () => void;
  play: (cue: Cue) => void;
};
const SoundContext = createContext<SoundState>({
  enabled: false,
  ready: false,
  toggle: () => {},
  play: () => {},
});

function audioConstructor() {
  return (
    window.AudioContext ??
    (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  );
}

/** Opt-in, local audio with one voice and no queued playback after cancellation. */
export function MatchdaySoundProvider({ children }: { children: ReactNode }) {
  const [enabled, setEnabled] = useState(false);
  const [ready, setReady] = useState(false);
  const allowed = useRef(false);
  const context = useRef<AudioContext | null>(null);
  const buffers = useRef(new Map<Cue, Promise<AudioBuffer>>());
  const pending = useRef<AbortController | null>(null);
  const active = useRef<{ source: AudioBufferSourceNode; gain: GainNode } | null>(null);
  const sequence = useRef(0);

  const stop = useCallback(() => {
    sequence.current++;
    const voice = active.current;
    active.current = null;
    if (voice) {
      voice.source.stop();
      voice.source.disconnect();
      voice.gain.disconnect();
    }
  }, []);

  const play = useCallback(
    (cue: Cue) => {
      if (!allowed.current || document.hidden) return;
      const Constructor = audioConstructor();
      if (!Constructor) return;
      stop();
      const ticket = sequence.current;
      const engine = context.current ?? (context.current = new Constructor());
      pending.current ??= new AbortController();
      let buffer = buffers.current.get(cue);
      if (!buffer) {
        buffer = fetch(sounds[cue].file, { signal: pending.current.signal })
          .then((response) => {
            if (!response.ok) throw new Error("Sound unavailable");
            return response.arrayBuffer();
          })
          .then((bytes) => engine.decodeAudioData(bytes));
        buffers.current.set(cue, buffer);
      }
      void Promise.all([engine.resume(), buffer])
        .then(([, decoded]) => {
          if (!allowed.current || document.hidden || ticket !== sequence.current) return;
          const source = engine.createBufferSource();
          const gain = engine.createGain();
          source.buffer = decoded;
          gain.gain.value = sounds[cue].gain;
          source.connect(gain);
          gain.connect(engine.destination);
          active.current = { source, gain };
          source.onended = () => {
            if (active.current?.source === source) active.current = null;
            source.disconnect();
            gain.disconnect();
          };
          source.start();
        })
        .catch(() => {
          // Audio is optional: a blocked or unavailable cue never blocks navigation.
          buffers.current.delete(cue);
        });
    },
    [stop],
  );

  const toggle = useCallback(() => {
    allowed.current = !allowed.current;
    setEnabled(allowed.current);
    if (allowed.current) play("menu");
    else {
      stop();
      void context.current?.suspend().catch(() => {});
    }
  }, [play, stop]);

  useEffect(() => {
    setReady(Boolean(audioConstructor()));
    const visibility = () => {
      if (document.hidden) {
        stop();
        void context.current?.suspend().catch(() => {});
      }
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") stop();
    };
    window.addEventListener(FOOTBALL_RESET_EVENT, stop);
    window.addEventListener("keydown", escape);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      allowed.current = false;
      stop();
      pending.current?.abort();
      void context.current?.close().catch(() => {});
      window.removeEventListener(FOOTBALL_RESET_EVENT, stop);
      window.removeEventListener("keydown", escape);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [stop]);

  const value = useMemo(() => ({ enabled, ready, toggle, play }), [enabled, ready, toggle, play]);
  return <SoundContext.Provider value={value}>{children}</SoundContext.Provider>;
}

export const useMatchdaySound = () => useContext(SoundContext);

const Toggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  min-height: 44px;
  padding: 10px 14px;
  border: 1px solid ${({ theme }) => theme.colors.line};
  color: ${({ theme }) => theme.colors.gold};
  background: transparent;
  font-size: 0.75rem;
  &:not(:disabled):is(:hover, :focus-visible) {
    background: #e8cd7310;
    border-color: ${({ theme }) => theme.colors.gold};
    color: #f2dfa0;
  }
  &:not(:disabled):active {
    background: #e8cd7322;
  }
  &[data-compact="true"] {
    flex-shrink: 0;
    flex-direction: column;
    gap: 3px;
    width: 44px;
    min-height: 54px;
    padding: 0;
    border: 0;
    font-size: 0.5rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
`;

export function SoundToggle({ compact = false }: { compact?: boolean }) {
  const { enabled, ready, toggle } = useMatchdaySound();
  const Icon = enabled ? Volume2 : VolumeX;
  return (
    <Toggle
      type="button"
      data-compact={compact}
      data-sound-toggle
      aria-label={`Turn stadium sounds ${enabled ? "off" : "on"}`}
      aria-pressed={enabled}
      disabled={!ready}
      onClick={toggle}
      title="Optional football sounds for menu clicks and the mini-game"
    >
      <Icon size={compact ? 19 : 17} strokeWidth={1.4} aria-hidden />
      <span>
        {compact
          ? enabled
            ? "Sound on"
            : "Sound off"
          : `Stadium sounds: ${enabled ? "on" : "off"}`}
      </span>
    </Toggle>
  );
}
